import { FigureDataDto, FigureGroupDataDto, FigureSettingsDto } from "../types";
import { fillPropertiesArray } from "./fillPropertiesArray";

/**
 * This function ensures that the FigureDataDto has all the required fields for its figure type
 * and fills in any missing optional fields with default values.
 *
 * @param figureInput - The input FigureDataDto object to preprocess
 * @param figureSettings - Settings related to all figure types
 * @returns - The preprocessed FigureDataDto object
 */
export const preProcessFigureInput = (
  figureSettings: FigureSettingsDto,
  figureInput?: FigureDataDto,
): FigureDataDto => {
  const preProcessedFigureInput = { ...(figureInput ?? {}) };

  // Throw if there are no figure type settings
  if (figureSettings.figureTypeSettings.length === 0) {
    throw new Error("preProcessFigureInput: No figure type settings available in figureSettings");
  }

  // Check id exists, if not create one
  if (!preProcessedFigureInput.id) {
    preProcessedFigureInput.id = crypto.randomUUID();
  }

  // Check figureType exists in settings or take the first
  let figureTypeSetting = figureSettings.figureTypeSettings.find(
    (ft) => ft.value === preProcessedFigureInput.figureType,
  );
  if (!figureTypeSetting) {
    console.warn(
      `preProcessFigureInput: figureType "${preProcessedFigureInput.figureType}" not found in figureSettings`,
    );
    figureTypeSetting = figureSettings.figureTypeSettings[0];
    preProcessedFigureInput.figureType = figureTypeSetting.value;
  }

  // Check legendType exists in settings or take the first
  let legendSetting = figureSettings.figureLegendSettings.find(
    (ls) => ls.legendType === preProcessedFigureInput.legendType,
  );
  if (!legendSetting) {
    console.warn(
      `preProcessFigureInput: legend type "${preProcessedFigureInput.legendType}" not found in figureSettings`,
    );
    legendSetting = figureSettings.figureLegendSettings[0];
    preProcessedFigureInput.legendType = legendSetting.legendType;
  }

  // Check all required lists exist, if not create empty
  if (!preProcessedFigureInput.properties) preProcessedFigureInput.properties = [];
  if (!preProcessedFigureInput.figureTypeProperties)
    preProcessedFigureInput.figureTypeProperties = [];
  if (!preProcessedFigureInput.axes) preProcessedFigureInput.axes = [];
  if (!preProcessedFigureInput.dataGroups) preProcessedFigureInput.dataGroups = [];
  if (!preProcessedFigureInput.legend) preProcessedFigureInput.legend = [];
  if (!preProcessedFigureInput.referenceLines) preProcessedFigureInput.referenceLines = [];
  if (!preProcessedFigureInput.regressionLines) preProcessedFigureInput.regressionLines = [];

  const processedFigureInput = preProcessedFigureInput as FigureDataDto;

  // Check all required figure settings exist
  processedFigureInput.properties = fillPropertiesArray(
    figureSettings.figureProperties,
    processedFigureInput.properties,
  );
  processedFigureInput.figureTypeProperties = fillPropertiesArray(
    figureTypeSetting.properties,
    processedFigureInput.figureTypeProperties,
  );

  // Ensure there is exactly one legend entry and that it has all required properties
  if (processedFigureInput.legend.length > 1) {
    console.warn(
      `preProcessFigureInput: More than one legend entry found, only the first will be used. Found ${processedFigureInput.legend.length} entries.`,
    );
    processedFigureInput.legend = [processedFigureInput.legend[0]];
  }
  // Ensure legend array has at least one entry
  if (processedFigureInput.legend.length === 0) {
    processedFigureInput.legend.push({
      id: crypto.randomUUID(),
      legendLabel: legendSetting.legendType,
      properties: [],
    });
  }
  processedFigureInput.legend[0].properties = fillPropertiesArray(
    legendSetting.parameters,
    processedFigureInput.legend[0].properties,
  );

  // Check all required axes exist and have required fields
  const requiredAxes = figureTypeSetting.requiredAxis;
  requiredAxes.forEach((axisType) => {
    const axisSetting = figureSettings.figureAxisSettings.find((ats) => ats.axisType === axisType);
    if (!axisSetting) {
      throw new Error(
        `preProcessFigureInput: required axis "${axisType}" not found in figureSettings`,
      );
    }
    let axis = processedFigureInput.axes.find((a) => a.axisType === axisType);
    if (!axis) {
      axis = {
        id: crypto.randomUUID(),
        axisType: axisType,
        properties: fillPropertiesArray(axisSetting.parameters),
      };
      processedFigureInput.axes.push(axis);
    } else {
      axis.properties = fillPropertiesArray(axisSetting.parameters, axis.properties);
    }
  });

  // Check all required properites for dataGroups
  if (processedFigureInput.dataGroups.length > 0)
    processedFigureInput.dataGroups = processedFigureInput.dataGroups.map((dg) => ({
      ...dg,
      properties: fillPropertiesArray(figureTypeSetting.dataRowVars, dg.properties),
    }));
  // Add default number of dataGroups if none exist
  if (processedFigureInput.dataGroups.length === 0) {
    const numberOfSeries = figureTypeSetting.defaultNumberOfSeries ?? 1;
    for (let i = 0; i < numberOfSeries; i++) {
      const thisDefaultSeries =
        figureTypeSetting.defaultSeries?.[
          Math.floor(i % (figureTypeSetting.defaultSeries?.length ?? 1))
        ];
      const newDataGroup: FigureGroupDataDto = {
        id: crypto.randomUUID(),
        dataGroupName: `${thisDefaultSeries?.dataGroupName ?? "DataGroup_"}${i + 1}`,
        dataGroupLabel: `${thisDefaultSeries?.dataGroupLabel ?? "Data Group"} ${i + 1}`,
        properties: fillPropertiesArray(
          figureTypeSetting.dataRowVars,
          thisDefaultSeries?.properties,
        ),
      };
      processedFigureInput.dataGroups.push(newDataGroup);
    }
  }

  // Check all required properites for regressionLines
  processedFigureInput.regressionLines = processedFigureInput.regressionLines.map((rl) => {
    const rlSettings = figureSettings.figureRegressionLineSettings.find(
      (rls) => rls.lineType === rl.lineType,
    );
    if (!rlSettings) {
      throw new Error(
        `preProcessFigureInput: regression line type "${rl.lineType}" not found in figureSettings`,
      );
    }
    return {
      ...rl,
      properties: fillPropertiesArray(rlSettings.parameters, rl.properties),
    };
  });

  return processedFigureInput;
};
